import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../utils/date';
import { getImpersonationDomains, type ImpersonationDomain } from '../services/api';
import LanguageSwitcher from '../components/LanguageSwitcher';

export function ImpersonationDomains() {
  const { t } = useTranslation();
  const [domains, setDomains] = useState<ImpersonationDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [brandFilter, setBrandFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<
    'suspected' | 'confirmed' | 'dismissed' | ''
  >('');

  const loadDomains = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getImpersonationDomains({
        brandCode: brandFilter || undefined,
        status: statusFilter || undefined,
        limit: 50,
      });
      setDomains(data.domains);
    } catch (error) {
      console.error('Failed to load impersonation domains:', error);
    } finally {
      setLoading(false);
    }
  }, [brandFilter, statusFilter]);

  useEffect(() => {
    loadDomains();
  }, [loadDomains]);

  const getClassificationBadge = (score: number) => {
    if (score >= 70) return 'bg-red-100 text-red-800';
    if (score >= 30) return 'bg-orange-100 text-orange-800';
    return 'bg-green-100 text-green-800';
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-red-100 text-red-800';
      case 'suspected':
        return 'bg-yellow-100 text-yellow-800';
      case 'dismissed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatSignals = (signals: string[]) => {
    const translations: Record<string, string> = {
      token_mismatch: t('brandImpersonation.signals.tokenMismatch'),
      typosquatting: t('brandImpersonation.signals.typosquatting'),
      high_similarity: t('brandImpersonation.signals.highSimilarity'),
      homoglyph: t('brandImpersonation.signals.homoglyph'),
      http_protocol: t('brandImpersonation.signals.httpProtocol'),
      suspicious_tld: t('brandImpersonation.signals.suspiciousTld'),
      official_domain: t('brandImpersonation.signals.officialDomain'),
    };

    return signals
      .map((signal) => translations[signal] || signal)
      .join(', ');
  };

  return (
    <div className="page-container">
      <div className="content-wrapper--wide">
        <div className="mb-4 flex items-center justify-end">
          <LanguageSwitcher />
        </div>
        <h1 className="page-title">{t('impersonationDomains.title')}</h1>
        <p className="text-gray-600 mb-6">{t('impersonationDomains.description')}</p>

        {/* Filters */}
        <div className="mb-6 flex flex-wrap gap-4">
          <div>
            <label htmlFor="brandFilter" className="block text-sm font-medium text-gray-700 mb-1">
              {t('impersonationDomains.filters.brand')}
            </label>
            <input
              id="brandFilter"
              type="text"
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value.toLowerCase())}
              placeholder={t('impersonationDomains.filters.brandPlaceholder')}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label htmlFor="statusFilter" className="block text-sm font-medium text-gray-700 mb-1">
              {t('impersonationDomains.filters.status')}
            </label>
            <select
              id="statusFilter"
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(
                  e.target.value as 'suspected' | 'confirmed' | 'dismissed' | ''
                )
              }
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">{t('impersonationDomains.filters.allStatuses')}</option>
              <option value="suspected">{t('impersonationDomains.status.suspected')}</option>
              <option value="confirmed">{t('impersonationDomains.status.confirmed')}</option>
              <option value="dismissed">{t('impersonationDomains.status.dismissed')}</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setBrandFilter('');
                setStatusFilter('');
              }}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
            >
              {t('impersonationDomains.filters.clear')}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-500">{t('common.loading')}</div>
        ) : domains.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {t('impersonationDomains.noResults')}
          </div>
        ) : (
          <>
            <div className="mb-4 text-sm text-gray-600">
              {t('impersonationDomains.resultsCount', { count: domains.length })}
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('impersonationDomains.table.domain')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('impersonationDomains.table.brand')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('impersonationDomains.table.score')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('impersonationDomains.table.status')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('impersonationDomains.table.hits')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('impersonationDomains.table.firstSeen')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('impersonationDomains.table.lastSeen')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {domains.map((domain) => (
                    <tr key={domain.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {domain.observedDomain}
                        </div>
                        {domain.closestTrustedDomain && (
                          <div className="text-xs text-gray-500">
                            → {domain.closestTrustedDomain}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {domain.matchedBrandName || domain.matchedBrandCode || '—'}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getClassificationBadge(domain.finalScore)}`}
                        >
                          {domain.finalScore}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(domain.status)}`}
                        >
                          {t(`impersonationDomains.status.${domain.status}`)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {domain.hitCount}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatDateTime(domain.firstSeenAt)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatDateTime(domain.lastSeenAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Details section */}
            <div className="mt-8 space-y-4">
              {domains.slice(0, 5).map((domain) => (
                <details key={domain.id} className="card-compact">
                  <summary className="cursor-pointer font-medium text-gray-900 hover:text-blue-600">
                    {domain.observedDomain} — {t('brandImpersonation.classification.Suspicious')}
                    {domain.closestTrustedDomain && (
                      <span className="text-gray-600 font-normal">
                        {' '}({domain.typoDistance !== undefined ? `${domain.typoDistance} ${t('impersonationDomains.details.typoDistance')}` : ''}{domain.jaroWinkler ? `; ${(domain.jaroWinkler * 100).toFixed(0)}% ${t('impersonationDomains.details.similarity')}` : ''})
                        {' '}{t('brandImpersonation.suggestion')} "{domain.closestTrustedDomain}"?
                      </span>
                    )}
                  </summary>
                  <div className="mt-4 space-y-2 text-sm text-gray-600">
                    <p>
                      <strong>{t('impersonationDomains.details.exampleUrl')}:</strong>{' '}
                      <a
                        href={domain.exampleUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline break-all"
                      >
                        {domain.exampleUrl}
                      </a>
                    </p>
                    {domain.signals.length > 0 && (
                      <p>
                        <strong>{t('impersonationDomains.details.signals')}:</strong>{' '}
                        {formatSignals(domain.signals)}
                      </p>
                    )}
                    {domain.typoDistance !== undefined && (
                      <p>
                        <strong>{t('impersonationDomains.details.typoDistance')}:</strong>{' '}
                        {domain.typoDistance}
                      </p>
                    )}
                    {domain.jaroWinkler !== undefined && (
                      <p>
                        <strong>{t('impersonationDomains.details.similarity')}:</strong>{' '}
                        {(domain.jaroWinkler * 100).toFixed(1)}%
                      </p>
                    )}
                    {domain.idnHomographSuspected && (
                      <p className="text-orange-600">
                        <strong>{t('impersonationDomains.details.idnWarning')}:</strong>{' '}
                        {domain.punycodeHostname}
                      </p>
                    )}
                  </div>
                </details>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
